import { DownOutlined, LoadingOutlined } from "@ant-design/icons";
import LinkIcon from "components/svg-icons/link-icon";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import Search from "antd/lib/input/Search";
import { Button, Dropdown, Space, Tooltip } from "antd";
import { ProjectContext } from "context/ProjectProvider";
import { useHistory, useLocation } from "react-router";
import { ErrorMessages } from "constants/index";
import { ENTITIES_DROPDOWN_NAMES } from "../queries/queries";
import {
  DropDownDBFeatureInstance,
  DropdownEntityOption,
  TFeatureDetail
} from "../models";
import { translateFeatureToOptions } from "../utils";

function DropDownEntityNames({
  featureDetail,
  selectedDropdownEntity,
  setSelectedDropdownEntity,
  disable
}: {
  featureDetail: TFeatureDetail;
  selectedDropdownEntity: DropdownEntityOption | undefined;
  setSelectedDropdownEntity: (entity: DropdownEntityOption) => void;
  disable: boolean;
}) {
  const { search } = useLocation();
  const history = useHistory();
  const location = useLocation();
  const { gqlClientForProject } = useContext(ProjectContext);
  const [searchText, setSearchText] = useState("");
  const [open, setOpen] = useState(false);
  const [entities, setEntities] = useState<Array<DropdownEntityOption>>();

  const { data: dropdownEntities, loading } = useCIQQuery<{
    feature_instance: Array<DropDownDBFeatureInstance>;
  }>(ENTITIES_DROPDOWN_NAMES, {
    variables: {
      feature_type_id: featureDetail.featureTypeId,
      excluded_instance_id: []
    },
    client: gqlClientForProject,
    skip: !gqlClientForProject
  });

  useEffect(() => {
    if (entities?.length) {
      try {
        const urlParams = new URLSearchParams(search);
        const entityId = urlParams.get("entityId");
        const materialId = urlParams.get("materialId");
        const submittalId = urlParams.get("SubmittalId");

        const id = entityId || materialId || submittalId;

        if (id) {
          const obj = entities.find((item) => item.id === id);
          if (obj) {
            setSelectedDropdownEntity(obj);
          }
        }
      } catch (e) {
        console.error("Error parsing entityId from URL", e);
      }
    }
  }, [entities, search, setSelectedDropdownEntity]);

  useEffect(() => {
    if (dropdownEntities?.feature_instance) {
      setEntities(translateFeatureToOptions(dropdownEntities.feature_instance));
    }
  }, [dropdownEntities]);

  const getLevelUIOfItem = useCallback(
    (item: DropdownEntityOption) => {
      return (
        <Tooltip
          className="flex items-center w-[410px]"
          data-testid={`dropdownentitynames-tooltip-${item.id}`}
          title={
            disable ? null : (
              <div className="space-y-1 py-1">
                <i className=" text-ciq-gray-800 text-opacity-80 entity-id-with-spec">
                  {item.pre}
                </i>
                <div>{item?.title || ""}</div>
              </div>
            )
          }
        >
          <div className="flex grow items-start justify-start truncate space-x-2">
            <i className="entity-id-with-spec">{item.pre}</i>
            <span>{item?.title || ""}</span>
          </div>
          {item?.isLinked ? <LinkIcon /> : ""}
        </Tooltip>
      );
    },
    [disable]
  );

  const overlayMenu = useMemo(() => {
    if (loading) {
      return (
        <div
          className="linking-container--nav__loading h-20"
          data-testid="dropdownentitynames-loading"
        >
          <LoadingOutlined />
        </div>
      );
    }
    if (!entities?.length) {
      return (
        <div
          className="linking-container--nav__loading h-20"
          data-testid="dropdownentitynames-no-data"
        >
          No {featureDetail.title} Available
        </div>
      );
    }
    return (
      <div className="linking-container--nav__dropdown">
        <div className="linking-container--nav__dropdown--search">
          <Search
            className="linking-search"
            placeholder={`Search ${featureDetail.title} Here`}
            value={searchText}
            onChange={(e: any) => setSearchText(e.currentTarget.value)}
            data-testid="dropdownentitynames-search"
          />
        </div>
        {entities
          .filter((item) => {
            const label = item?.label?.toLocaleLowerCase();
            return label.includes(searchText.toLocaleLowerCase());
          })
          .map((item) => (
            <Space
              className="linking-container--nav__option flex items-center justify-between"
              key={item.id}
              onClick={() => {
                setOpen(false);
                setSearchText("");
                history.replace(`${location.pathname}?entityId=${item.id}`);
              }}
              data-testid={`dropdownentitynames-option-${item.id}`}
            >
              {getLevelUIOfItem(item)}
            </Space>
          ))}
      </div>
    );
  }, [
    entities,
    featureDetail.title,
    getLevelUIOfItem,
    history,
    loading,
    location.pathname,
    searchText
  ]);

  return (
    <Tooltip title={disable ? ErrorMessages.EditingLinkingMessage : null}>
      <Dropdown
        overlay={overlayMenu}
        className="mx-4 linking-container--nav__select"
        trigger={["click"]}
        open={open}
        onOpenChange={(op) => {
          setOpen(op);
        }}
        disabled={disable}
        data-testid="dropdownentitynames-dropdown"
      >
        <Button data-testid="dropdownentitynames-button">
          {selectedDropdownEntity ? (
            <Space className="w-[425px] flex justify-between">
              {getLevelUIOfItem(selectedDropdownEntity)}
              <DownOutlined className="" />
            </Space>
          ) : (
            <div
              className="w-[425px] flex justify-between items-center"
              data-testid="dropdownentitynames-select-a-entity"
            >
              <div>Select a {featureDetail.title}</div>
              <DownOutlined className="" />
            </div>
          )}
        </Button>
      </Dropdown>
    </Tooltip>
  );
}
export default DropDownEntityNames;
